import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { ExperimentFormatPipe } from 'src/app/pipes/experiment-format.pipe';
import { AppStateService } from 'src/app/services/app-state.service';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { FeatureFlags } from 'src/app/services/configuration.service.model';
import { LabpartnerService } from 'src/app/services/labpartner.service';
import {
  AssayName,
  Bundle,
  BundleStudyType,
  BundleStudyTypes,
  Experiment,
  ExperimentExtended,
  NotesCountMap,
  StudyType,
  StudyTypes,
  User,
} from 'src/app/services/labpartner.service.model';
import { EVENT_CREATE_BUNDLE, PAGE_NAME_EXPERIMENTSLIST } from 'src/app/services/logging-constants';
import { LoggingService } from 'src/app/services/logging.service';
import { UserAccountService } from 'src/app/services/user-account.service';
import { BundleService } from 'src/app/shared/bundle.service';
import { ExperimentService } from 'src/app/shared/experiment.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { BaseComponent } from 'src/app/support/base.component';
import { BundleComponent } from '../bundle/bundle.component';

@Component({
  selector: 'app-experiment-list-for-bundle',
  templateUrl: './experiment-list-for-bundle.component.html',
  styleUrls: ['./experiment-list-for-bundle.component.scss'],
  standalone: false,
})
export class ExperimentListForBundleComponent extends BaseComponent implements OnInit, OnDestroy {
  public experiments!: Experiment[];
  public objectName = 'Experiment';
  public CanCreate = false;
  public displayedColumns = [
    'actionsAdd',
    'experimentId',
    'name',
    'protocolNumber',
    'studyType',
    'assayName',
    'createdByName',
    'device',
    'dateCreated',
  ];
  public selectedDisplayedColumns = [
    'experimentId',
    'name',
    'protocolNumber',
    'studyType',
    'assayName',
    'actionsRemove',
  ];
  @ViewChild(MatTable) table!: MatTable<any>;
  experimentsData!: MatTableDataSource<any>;
  bundleData!: MatTableDataSource<any>;
  @ViewChild('allExperimentsSort') allExperimentsSort!: MatSort;
  @ViewChild('bundleExperimentsSort') bundleExperimentsSort!: MatSort;
  sort!: MatSort;
  @ViewChild('paginatorAllExperiments') paginatorAllExperiments!: MatPaginator;
  @ViewChild('paginatorBundleExperiments') paginatorBundleExperiments!: MatPaginator;

  paginator!: MatPaginator;
  searchKey: string = '';
  devices!: any;
  status!: any;
  allExperimentNoteCounts?: NotesCountMap;

  currentExperiment: Experiment | null = null;
  selectedExperiments: Experiment[] = [];
  currentUser?: User;
  currentRoles: string[] = [];
  wasCanceled: boolean = false;
  isCurtainBlocked: boolean = false;
  isClinicalTrialChecked: boolean = false;
  showClinicalTrial: boolean = false;
  disableClinicalTrialCheckBox: boolean = false;
  private readonly _destroying$ = new Subject<void>();
  selectedBundleTypeId: number = 1;
  bundleId: number | undefined;
  bundleExperimentIds!: string;

  StudyType = StudyType;
  studyTypes: any[] = StudyTypes;
  BundleStudyType = BundleStudyType;
  bundleStudyTypes: any[] = BundleStudyTypes;

  assayNames: AssayName[] = [];

  featureFlags$!: Observable<FeatureFlags | null>;

  constructor(
    public configService: ConfigurationService,
    public bundleService: BundleService,
    protected experimentService: ExperimentService,
    protected apiService: LabpartnerService,
    public accountService: UserAccountService,
    public appState: AppStateService,
    protected notificationService: NotificationService,
    protected loggingService: LoggingService,
    protected experimentPipe: ExperimentFormatPipe,
    public dialogRef: MatDialogRef<BundleComponent>,
  ) {
    super();
    this.featureFlags$ = this.configService.featureFlags$;
  }

  async ngOnInit() {
    /********************** Experiments ************************/
    await this.accountService.SetUserList();
    this.accountService.currentUser$
      .pipe(
        takeUntil(this._destroying$),
        filter(u => u != undefined),
        switchMap(u => {
          this.currentUser = u!;
          return this.apiService.getExperimentsList();
        }),
      )
      .subscribe(experiments => this.parseExperimentsForDisplay(experiments));
    this.loggingService.logPageView(PAGE_NAME_EXPERIMENTSLIST);

    /***********************  Bundles ************************/
    this.selectedBundleTypeId = this.bundleService.form.value.bundleType;
    this.bundleId = this.bundleService.form.value.$key;
    this.bundleExperimentIds = this.bundleService.form.value.experimentIds;
    this.showClinicalTrial = this.accountService.isClinicalTrial(); //if clincial trial role

    if (this.bundleService.form.value.isClinicalTrial) {
      this.isClinicalTrialChecked = true;
      this.disableClinicalTrialCheckBox = true;
    }

    this.subscription.add(
      this.bundleService.form.valueChanges.pipe(distinctUntilChanged()).subscribe(() => {
        // this.setIsValid();    // used for form debugging
      }),
    );

    this.subscription.add(
      this.accountService.currentRoles$.subscribe(roles => {
        this.currentRoles = roles;
      }),
    );

    this.subscription.add(
      this.apiService.getAssayNames(true).subscribe(assayNames => {
        this.assayNames = assayNames.filter(x => x.isActive);
        this.assayNames.forEach(
          x => (x.labelName = `${x.assayId}.${x.assayRevision} - ${x.shortName}`),
        );
        if (this.assayNames.length > 0) {
          if (!this.bundleId) {
            this.bundleService.form.controls.assayNameId.setValue(this.assayNames[0].id);
          }
        }
      }),
    );
  }

  setIsValid(): void {
    const form = this.bundleService.form;
    const isFormValid = form.valid;
    if (!isFormValid) {
      this.displayInvalidFormFields(form);
    }
  }

  filterInitialExperiments() {}

  private parseExperimentsForDisplay(experiments: Experiment[]) {
    this.experiments = experiments;
    this.filterInitialExperiments();
    for (const experiment of this.experiments as ExperimentExtended[]) {
      experiment.createdByName = this.accountService.getUserName(experiment.createdBy);
      experiment.deviceName = this.experimentService.getDeviceName(experiment.deviceType);
      experiment.expStatus = this.experimentService.getStatus(experiment.status);
    }

    // populates table with all experiments
    this.experimentsData = new MatTableDataSource(this.experiments);
    this.experimentsData.sort = this.allExperimentsSort;
    this.experimentsData.paginator = this.paginatorAllExperiments;
    this.experimentsData.filterPredicate = (data, filter) => {
      return this.displayedColumns.some((ele: string) => {
        if (data[ele] == undefined && ele != 'device' && ele != 'assayName') {
          return false;
        }
        let value = data[ele];
        if (ele == 'experimentId') {
          value = this.experimentPipe.transform(data.experimentId, data.type);
        } else if (ele == 'studyType') {
          value = this.getStudyTypeName(data[ele]);
        } else if (ele == 'assayName' && data['assayNameId'] != null) {
          value = this.getAssayName(data['assayNameId']);
        } else if (ele == 'device' && data['deviceType'] != null) {
          value = data['deviceName'];
        }
        return value?.toString().toLowerCase().indexOf(filter) != -1;
      });
    };

    // populates table with user added experiments
    if (this.bundleId == null) {
      this.bundleData = new MatTableDataSource();
    } else {
      this.selectedExperiments = this.getExperimentsByExperimentId(this.bundleExperimentIds);
      this.bundleData = new MatTableDataSource(this.selectedExperiments);

      this.filterExperimentsByStudyType(false);
    }

    this.bundleData.sort = this.bundleExperimentsSort;
    this.bundleData.paginator = this.paginatorBundleExperiments;
    this.bundleData.filterPredicate = (data, filter) => {
      return this.displayedColumns.some(ele => {
        return (
          data[ele] != undefined &&
          ele != 'actions' &&
          data[ele].toString().toLowerCase().indexOf(filter) != -1
        );
      });
    };
  }

  getExperimentsByExperimentId(bundleExperimentIds: string): Experiment[] {
    const selectedExperiments: Experiment[] = [];
    const experimentIds = bundleExperimentIds.split(',').map(Number);
    experimentIds.forEach(experimentId => {
      const experiment = this.experiments.filter(x => {
        return x.experimentId === experimentId;
      });

      selectedExperiments.push(experiment[0]);
    });

    return selectedExperiments;
  }

  protected ngOnDestroyInternal(): void {
    // required by base component. clean up any component specific resources
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  onSearchClear() {
    setTimeout(() => {
      this.searchKey = '';
      this.onChange();
    });
  }

  onChange() {
    this.experimentsData.filter = this.searchKey.trim().toLowerCase();
  }

  onCancel() {
    this.wasCanceled = true;
    this.notificationService.warn(':: Canceled');
    const dialogresult = { ok: false, data: null };
    if (this.selectedExperiments.length < 1) {
      this.onClose(dialogresult);
    }
    this.isCurtainBlocked = false;
    this.dialogRef.close(dialogresult);
  }

  onClose(dialogresult: any) {
    this.bundleService.form.reset();
    this.bundleService.initializeExperimentFormGroup();
    this.isCurtainBlocked = false;
    this.dialogRef.close(dialogresult);
  }

  toggleClinicalStatus(): void {
    this.isClinicalTrialChecked = !this.isClinicalTrialChecked;
    console.log('isClinicalTrialChecked: ', this.isClinicalTrialChecked);
  }

  onSubmit(data: any) {
    this.isCurtainBlocked = true;
    if (this.wasCanceled) {
      return;
    }
    if (this.isClinicalTrialChecked) {
      data.isClinicalTrial = true;
    }
    console.log(data);

    if (!this.bundleService.form.get('$key')!.value) {
      console.log('inserting new record');
      const sub = this.apiService.createExperimentIdsBundle(data).subscribe(
        (result: Bundle) => {
          this.notificationService.success(':: Bundle created successfully');
          const dialogresult = { ok: true, data: result };
          this.onClose(dialogresult);

          const props: { [key: string]: any } = {
            BundleId: result.bundleId,
            IsUsedToGenerateReport: result.isUsedToGenerateReport,
            BundleStudyType: '',
            AssayName: '',
          };

          if (result.bundleStudyType != null) {
            props.BundleStudyType = this.bundleStudyTypes.find(
              x => x.id == result.bundleStudyType,
            )?.name;
            props.AssayName = this.assayNames.find(x => x.id == result.assayNameId)?.name;
          }

          this.loggingService.logEvent(EVENT_CREATE_BUNDLE, props);
        },
        (err: any) => {
          // TODO: show an error with toast, etc.
          this.notificationService.warn(':: Error creating Bundle');
          console.log(err);
          const dialogresult = { ok: false, data: err };
          this.onClose(dialogresult);
        },
      );
      this.subscription.add(sub);
    } else {
      console.log('updating existing record');

      const sub = this.apiService
        .updateBundle(this.bundleService.form.get('$key')!.value, data)
        .subscribe(
          (result: Bundle) => {
            this.notificationService.success(':: Bundle updated successfully');
            const dialogresult = { ok: true, data: result };
            this.onClose(dialogresult);
          },
          (err: any) => {
            // TODO: show an error with toast, etc.
            this.notificationService.warn(':: Error updating Bundle');
            const dialogresult = { ok: false, data: err };
            console.log(err);
            this.onClose(dialogresult);
          },
        );
      this.subscription.add(sub);
    }
  }

  showAddButton(row: any): boolean {
    return this.bundleData.data.includes(row);
  }

  onAdd(row: any) {
    this.bundleData.data = [...this.bundleData.data, row];
    this.selectedExperiments = [...this.selectedExperiments, row];
    const experimentIds = this.selectedExperiments.map(x => x.experimentId.toString());
    this.bundleExperimentIds = experimentIds.toString();
    this.bundleService.form.patchValue({
      experimentIds: this.bundleExperimentIds,
    });
    this.bundleData._updateChangeSubscription();

    if (
      this.bundleService.form.controls.isUsedToGenerateReport.value &&
      row.studyType == StudyType.DailyExternalControls
    ) {
      this.filterExperimentsByStudyType(false);
    }
  }

  onDelete(row: any) {
    const indexInBundleData = this.bundleData.data.findIndex(
      item => item.experimentId == row.experimentId,
    );
    if (indexInBundleData !== -1) {
      this.bundleData.data.splice(indexInBundleData, 1);
    }
    const indexInSelectedExperiments = this.selectedExperiments.findIndex(
      item => item.experimentId == row.experimentId,
    );
    if (indexInSelectedExperiments !== -1) {
      this.selectedExperiments.splice(indexInSelectedExperiments, 1);
    }
    const experimentIds = this.selectedExperiments.map(x => x.experimentId.toString());
    this.bundleExperimentIds = experimentIds.toString();
    this.bundleService.form.patchValue({
      experimentIds: this.bundleExperimentIds,
    });
    this.bundleData._updateChangeSubscription();

    if (
      this.bundleService.form.controls.isUsedToGenerateReport.value &&
      row.studyType == StudyType.DailyExternalControls
    ) {
      this.filterExperimentsByStudyType(true);
    }
  }

  displayInvalidFormFields(form: UntypedFormGroup): void {
    if (!form.valid) {
      const invalids = this.findInvalidControlsRecursive(form);
      console.log(invalids);
    }
  }

  // Returns an array of invalid control/group names, or a zero-length array if
  // no invalid controls/groups where found.
  findInvalidControlsRecursive(formToInvestigate: UntypedFormGroup | UntypedFormArray): string[] {
    const invalidControls: string[] = [];
    const recursiveFunc = (form: UntypedFormGroup | UntypedFormArray) => {
      Object.keys(form.controls).forEach(field => {
        const control = form.get(field);
        if (control?.invalid) {
          invalidControls.push(field);
        }
        if (control instanceof UntypedFormGroup) {
          recursiveFunc(control);
        } else if (control instanceof UntypedFormArray) {
          recursiveFunc(control);
        }
      });
    };
    recursiveFunc(formToInvestigate);
    return invalidControls;
  }

  isFormDisabled() {
    if (!this.bundleService.form.controls.isUsedToGenerateReport.value) {
      return this.bundleService.form.invalid;
    } else {
      return (
        this.bundleService.form.invalid ||
        this.selectedExperiments.length < 2 ||
        !this.selectedExperiments.some(x => x.studyType == StudyType.DailyExternalControls)
      );
    }
  }

  onChangeReportFlag() {
    this.selectedExperiments = [];
    this.bundleData.data = [];
    if (!this.bundleService.form.controls.isUsedToGenerateReport.value) {
      this.experimentsData.data = this.experiments;
    } else {
      this.experimentsData.data = [];
      this.bundleService.form.controls.bundleStudyType.setValue(this.bundleStudyTypes[0].id);
      this.onChangeStudyType();
    }

    this.experimentsData._updateChangeSubscription();
  }

  onChangeStudyType() {
    this.selectedExperiments = [];
    this.bundleData.data = [];
    if (this.bundleService.form.controls.bundleStudyType.value != null) {
      this.filterExperimentsByStudyType(true);
    }
  }

  onChangeAssayName() {
    this.selectedExperiments = [];
    this.bundleData.data = [];
    if (this.bundleService.form.controls.assayNameId.value != null) {
      this.filterExperimentsByStudyType(true);
    }
  }

  getStudyTypeName(id: number) {
    return this.studyTypes.find(x => x.id == id)?.name;
  }

  getAssayName(id: number) {
    const assay = this.assayNames.find(x => x.id == id);
    return assay ? `${assay.assayId}.${assay.assayRevision} - ${assay.shortName}` : '';
  }

  filterExperimentsByStudyType(showDailyExternalControls: boolean) {
    if (!this.bundleService.form.controls.isUsedToGenerateReport.value) {
      return;
    }
    switch (this.bundleService.form.controls.bundleStudyType.value) {
      case BundleStudyType.LoD:
        this.experimentsData.data = this.experiments.filter(
          x =>
            x.studyType == StudyType.LoDConfirmation ||
            x.studyType == StudyType.LoDRangeFinding ||
            (x.studyType == StudyType.DailyExternalControls && showDailyExternalControls),
        );
        break;
      case BundleStudyType.Precision:
        this.experimentsData.data = this.experiments.filter(
          x =>
            x.studyType == StudyType.Precision ||
            (x.studyType == StudyType.DailyExternalControls && showDailyExternalControls),
        );
        break;
    }
    if (this.bundleService.form.controls.assayNameId.value != null) {
      this.experimentsData.data = this.experimentsData.data.filter(
        x => x.assayNameId == this.bundleService.form.controls.assayNameId.value,
      );
    }
    this.experimentsData._updateChangeSubscription();
  }
}
